/**
 * ForwardFormationBot - Forward for IMPOSSIBLE mode.
 * Extends FormationBot with pressing, receiving, attacking, and shooting behaviors.
 */

import * as BABYLON from "babylonjs";
import { Ball } from "./Ball";
import { Player } from "./Player";
import { FormationBot } from "./FormationBot";
import { BotBase } from "./BotBase";
import { AIRole, AIState, AllowedZone, SeparationConfig } from "../systems/DifficultyManager";
import { KickType } from "./HumanoidMesh";

// Forward-specific constants
const SHOOT_RANGE = 22;
const PRESS_DISTANCE = 10;
const DRIBBLE_SPEED = 5.5;

/**
 * Formation forward bot for IMPOSSIBLE mode
 * - Zone: attacking third
 * - States: WAIT, RECEIVE, ATTACK, SHOOT, PRESS, FORMATION_RETURN
 * - Behavior: Stay forward, make runs, apply light pressure
 */
export class ForwardFormationBot extends FormationBot {
  private _pressTimer = 0;

  constructor(
    scene: BABYLON.Scene,
    ball: Ball,
    role: AIRole,
    homePosition: BABYLON.Vector3,
    allowedZone: AllowedZone,
    separationConfig: SeparationConfig,
    shadowGenerator?: BABYLON.ShadowGenerator,
    player?: Player
  ) {
    super(
      scene,
      ball,
      role,
      homePosition,
      allowedZone,
      separationConfig,
      shadowGenerator,
      player,
      `forwardFormation_${role}`
    );
    
    // Set stretch for forwards
    this.setStretch(10, 12);
  }

  /**
   * Red jersey color for formation forwards
   */
  protected _getJerseyColor(): BABYLON.Color3 {
    return new BABYLON.Color3(0.9, 0.2, 0.2); // Red jersey
  }

  /**
   * Update with press timer
   */
  update(dt: number, teammates: BotBase[]): void {
    if (this._pressTimer > 0) this._pressTimer -= dt;
    super.update(dt, teammates);
  }

  /**
   * Forward-specific decision making
   */
  protected _makeDecision(
    distToBall: number,
    distToGoal: number,
    ballPos: BABYLON.Vector3,
    myPos: BABYLON.Vector3
  ): void {
    // Check if ball is in our zone
    const ballInOurZone = ballPos.z >= this._allowedZone.minZ && ballPos.z <= this._allowedZone.maxZ;

    // If we have possession and are in shooting range
    if (this.hasBallPossession && distToGoal < SHOOT_RANGE && Math.abs(myPos.x) < 8) {
      this._state = AIState.SHOOT;
      return;
    }

    // If we have possession, attack toward goal
    if (this.hasBallPossession) {
      this._state = AIState.ATTACK;
      return;
    }

    // Tackle if player has ball and we're close
    if (this._player && this._player.hasBallPossession && distToBall < 2.5 && this._tackleCooldown <= 0) {
      this._state = AIState.TACKLE;
      return;
    }

    // If ball is in our zone, chase it
    if (ballInOurZone) {
      this._state = AIState.CHASE_BALL;
      return;
    }

    // If ball is ahead (toward opponent goal), receive
    if (ballPos.z < this._allowedZone.minZ) {
      this._state = AIState.RECEIVE;
      return;
    }

    // If player has ball in our defensive half, apply pressure
    if (this._player && this._player.hasBallPossession && 
        this._player.mesh.position.z < 0 && this._pressTimer <= 0) {
      this._state = AIState.PRESS;
      return;
    }

    // If ball is in midfield, wait for opportunity
    if (ballPos.z > 0) {
      this._state = AIState.WAIT;
      return;
    }

    // Check if should return to formation
    if (this._shouldReturnToFormation(ballPos, myPos)) {
      this._state = AIState.FORMATION_RETURN;
      return;
    }

    // Default: hold position
    this._state = AIState.HOLD_POSITION;
  }

  /**
   * Handle state behavior with forward-specific states
   */
  protected _handleState(
    ballPos: BABYLON.Vector3,
    myPos: BABYLON.Vector3,
    distToBall: number,
    distToGoal: number,
    dt: number
  ): void {
    switch (this._state) {
      case AIState.WAIT:
        this._handleWait(ballPos, myPos, dt);
        break;
      case AIState.RECEIVE:
        this._handleReceive(ballPos, myPos, distToBall, dt);
        break;
      case AIState.ATTACK:
        this._handleAttack(ballPos, myPos, distToGoal, dt);
        break;
      case AIState.PRESS:
        this._handlePress(ballPos, myPos, dt);
        break;
      default:
        super._handleState(ballPos, myPos, distToBall, distToGoal, dt);
    }
  }

  /**
   * Wait for pass opportunity
   */
  private _handleWait(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3, dt: number): void {
    // Get dynamic position
    let targetPos = this._getDynamicPosition(ballPos);
    
    // Stay high and ready for through ball
    targetPos.z = Math.min(targetPos.z, this._allowedZone.maxZ - 2);
    
    // Create space
    const idealX = this._formationAnchor.x + (ballPos.x > 0 ? 3 : -3);
    targetPos.x = BABYLON.Scalar.Lerp(targetPos.x, idealX, 0.3);
    
    // Clamp to zone
    targetPos.x = BABYLON.Scalar.Clamp(targetPos.x, this._allowedZone.minX, this._allowedZone.maxX);
    targetPos.z = BABYLON.Scalar.Clamp(targetPos.z, this._allowedZone.minZ, this._allowedZone.maxZ);
    
    const dx = targetPos.x - myPos.x;
    const dz = targetPos.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    
    if (dist > 0.5) {
      const speed = DRIBBLE_SPEED * 0.4;
      myPos.x += (dx / dist) * speed * dt;
      myPos.z += (dz / dist) * speed * dt;
      this._currentSpeed = speed;
    } else {
      this._currentSpeed = 0;
    }
    
    // Face the ball
    const toBall = ballPos.subtract(myPos);
    const angle = Math.atan2(toBall.x, toBall.z);
    this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.05);
  }

  /**
   * Receive ball behavior
   */
  private _handleReceive(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3, distToBall: number, dt: number): void {
    const ballVel = this._ball.mesh.physicsImpostor?.getLinearVelocity();
    let targetPos: BABYLON.Vector3;

    if (ballVel && ballVel.length() > 2) {
      // Predict ball position
      const ballDir = new BABYLON.Vector3(ballVel.x, 0, ballVel.z).normalize();
      const timeToIntercept = distToBall / Math.max(ballVel.length(), 1);
      targetPos = ballPos.add(ballDir.scale(timeToIntercept * 1.5));
    } else {
      targetPos = ballPos.clone();
    }

    // Clamp to zone
    targetPos.x = BABYLON.Scalar.Clamp(targetPos.x, this._allowedZone.minX, this._allowedZone.maxX);
    targetPos.z = BABYLON.Scalar.Clamp(targetPos.z, this._allowedZone.minZ, this._allowedZone.maxZ);

    const dx = targetPos.x - myPos.x;
    const dz = targetPos.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    if (dist > 0.5) {
      const speed = dist > 5 ? 7.5 : 5.5;
      myPos.x += (dx / dist) * speed * dt;
      myPos.z += (dz / dist) * speed * dt;
      this._currentSpeed = speed;
    } else {
      this._currentSpeed = 0;
    }

    const angle = Math.atan2(dx, dz);
    this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.15);

    // If close to ball, gain possession
    if (distToBall < 2.0) {
      this.hasBallPossession = true;
      this._state = AIState.ATTACK;
    }
  }

  /**
   * Attack toward goal
   */
  private _handleAttack(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3, distToGoal: number, dt: number): void {
    const goalPos = new BABYLON.Vector3(0, 0, -30);
    const toGoal = goalPos.subtract(myPos).normalize();

    // Dribble toward goal
    if (this._kickTimer <= 0) {
      const dribbleDir = new BABYLON.Vector3(toGoal.x * 0.5, 0.1, toGoal.z * 0.5).normalize();
      this._ball.kick(dribbleDir, 3.5);
      this._kickTimer = 0.35;
      this.animator.triggerKick(KickType.Normal);
    }

    // Move toward ball
    const dx = ballPos.x - myPos.x;
    const dz = ballPos.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    if (dist > 0.3) {
      myPos.x += (dx / dist) * DRIBBLE_SPEED * dt;
      myPos.z += (dz / dist) * DRIBBLE_SPEED * dt;
    }

    this._currentSpeed = DRIBBLE_SPEED;

    const angle = Math.atan2(toGoal.x, toGoal.z);
    this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.15);

    // If in shooting range, shoot
    if (distToGoal < SHOOT_RANGE && Math.abs(myPos.x) < 8) {
      this._state = AIState.SHOOT;
    }
  }

  /**
   * Apply light pressure when player has ball
   */
  private _handlePress(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3, dt: number): void {
    if (!this._player || !this._player.hasBallPossession) {
      this._state = AIState.HOLD_POSITION;
      return;
    }
    
    const playerPos = this._player.mesh.position;
    
    // Only press if player is in our pressing zone
    if (playerPos.z > 5) {
      this._state = AIState.HOLD_POSITION;
      return;
    }
    
    // Move toward player but stay in formation shape
    const targetPos = new BABYLON.Vector3(
      playerPos.x * 0.5,
      0,
      Math.max(playerPos.z - 3, this._allowedZone.maxZ - 5)
    );
    
    // Clamp to zone
    targetPos.x = BABYLON.Scalar.Clamp(targetPos.x, this._allowedZone.minX, this._allowedZone.maxX);
    targetPos.z = BABYLON.Scalar.Clamp(targetPos.z, this._allowedZone.minZ, this._allowedZone.maxZ);
    
    const dx = targetPos.x - myPos.x;
    const dz = targetPos.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    
    if (dist > 0.5) {
      const speed = DRIBBLE_SPEED * 0.8;
      myPos.x += (dx / dist) * speed * dt;
      myPos.z += (dz / dist) * speed * dt;
      this._currentSpeed = speed;
    } else {
      this._currentSpeed = 0;
    }
    
    // Face the player
    const toPlayer = playerPos.subtract(myPos);
    const angle = Math.atan2(toPlayer.x, toPlayer.z);
    this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.1);
    
    // Press cooldown
    this._pressTimer = 2.0;
  }

  /**
   * Reset press timer
   */
  resetPosition(): void {
    super.resetPosition();
    this._pressTimer = 0;
  }
}